import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { mockPersonalStats, mockTeamStats } from '../../data/mockRecognition';
import { fetchCommunityActivitySummary } from '../../services/liveSessions';

function buildCommunityDetails(summary) {
  if (!summary) return mockPersonalStats.pillars.community.details;
  const parts = [];
  if (summary.leads_captured > 0) parts.push(`${summary.leads_captured} Leads`);
  if (summary.faq_answers > 0) parts.push(`${summary.faq_answers} Respuestas FAQ`);
  if (summary.live_sessions_completed > 0) {
    parts.push(`${summary.live_sessions_completed} Sesiones en Vivo`);
  }
  return parts.length > 0 ? parts.join(' • ') : mockPersonalStats.pillars.community.details;
}

export default function RecognitionBoard() {
  const [viewLevel, setViewLevel] = useState('personal'); // 'personal' | 'team'
  const [communitySummary, setCommunitySummary] = useState(null);

  useEffect(() => {
    fetchCommunityActivitySummary()
      .then(setCommunitySummary)
      .catch(() => setCommunitySummary(null));
  }, []);

  const { pillars, recentBadges, isAgentOfTheMonth, currentMonth } = mockPersonalStats;
  const communityPillar = {
    ...pillars.community,
    current: communitySummary?.total_points ?? pillars.community.current,
    details: buildCommunityDetails(communitySummary),
  };
  const displayPillars = { ...pillars, community: communityPillar };

  return (
    <div className="w-full max-w-7xl mx-auto px-6 py-6 animate-fade-in">
      
      {/* Header & View Toggles */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-10">
        <div>
          <h2 className="text-2xl font-black text-[var(--text-main)] uppercase italic tracking-tight flex items-center gap-2">
            <SafeIcon name="Award" className="w-6 h-6 text-brand-gold" />
            Muro de Reconocimiento
          </h2>
          <p className="text-xs text-[var(--text-secondary)] font-medium mt-1 uppercase tracking-widest">
            Celebrando el crecimiento integral del movimiento.
          </p>
        </div>

        {/* View Toggle */}
        <div className="flex bg-[var(--bg-surface)] p-1 rounded-xl border border-[var(--border-subtle)] shadow-sm w-full md:w-auto">
          <button 
            onClick={() => setViewLevel('personal')}
            className={`flex-1 md:flex-none px-6 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
              viewLevel === 'personal' 
                ? 'gfi-gradient-bg text-brand-navy shadow-md' 
                : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
            }`}
          >
            Mi Progreso
          </button>
          <button 
            onClick={() => setViewLevel('team')}
            className={`flex-1 md:flex-none px-6 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
              viewLevel === 'team' 
                ? 'gfi-gradient-bg text-brand-navy shadow-md' 
                : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
            }`}
          >
            Mi Equipo (Embajador)
          </button>
        </div>
      </div>

      {viewLevel === 'personal' ? (
        <div className="space-y-8">
          {/* Main Celebration Card (If Agent of the Month or similar) */}
          {isAgentOfTheMonth && (
            <div className="relative overflow-hidden rounded-3xl p-8 md:p-10 shadow-xl border border-white/20 group">
              <div className="absolute inset-0 gfi-gradient-bg opacity-90" />
              <div className="absolute inset-0 bg-black/20" /> {/* Dark overlay for text contrast */}
              
              {/* Decorative shapes */}
              <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
              <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-white/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />

              <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 text-white">
                <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border-2 border-white/40 shadow-inner shrink-0">
                  <SafeIcon name="Award" className="w-12 h-12 text-white drop-shadow-md" />
                </div>
                <div className="text-center md:text-left">
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest border border-white/30 shadow-sm mb-4 inline-block">
                    Reconocimiento Mensual
                  </span>
                  <h3 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter leading-none mb-2 drop-shadow-lg">
                    Agente del Mes
                  </h3>
                  <p className="text-sm md:text-base font-bold text-white/90 uppercase tracking-widest">
                    ¡Felicidades por tu impacto integral en {currentMonth}!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* The 3 Pillars */}
          <div>
            <h3 className="text-sm font-black text-[var(--text-main)] uppercase tracking-[0.2em] mb-4 pl-2 border-l-2 border-brand-cyan">
              Mis 3 Pilares de Crecimiento
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(displayPillars).map(([key, pillar]) => {
                const percentage = Math.min(100, Math.round((pillar.current / pillar.target) * 100));
                return (
                  <div key={key} className="bg-[var(--bg-surface)] p-6 rounded-3xl border border-[var(--border-subtle)] shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${pillar.color}15`, color: pillar.color }}>
                        <SafeIcon name={pillar.icon} className="w-5 h-5" />
                      </div>
                      <h4 className="text-sm font-black text-[var(--text-main)] uppercase tracking-widest">{pillar.label}</h4>
                    </div>
                    
                    <div className="mb-2 flex items-end justify-between">
                      <span className="text-2xl font-black text-[var(--text-main)] tracking-tight">
                        {pillar.prefix || ''}{pillar.current.toLocaleString()}
                      </span>
                      <span className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-widest mb-1">
                        Meta: {pillar.prefix || ''}{pillar.target.toLocaleString()}{pillar.suffix || ''}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full h-2.5 bg-[var(--bg-base)] rounded-full overflow-hidden mb-3 border border-[var(--border-subtle)]">
                      <div 
                        className="h-full rounded-full transition-all duration-1000 relative overflow-hidden"
                        style={{ width: `${percentage}%`, backgroundColor: pillar.color }}
                      >
                        <div className="absolute inset-0 bg-white/20 w-full animate-[shimmer_2s_infinite]" />
                      </div>
                    </div>

                    {pillar.details && (
                      <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-widest text-center">
                        {pillar.details}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Badges / Insignias */}
          <div>
            <h3 className="text-sm font-black text-[var(--text-main)] uppercase tracking-[0.2em] mb-4 pl-2 border-l-2 border-brand-gold">
              Insignias Recientes
            </h3>
            <div className="flex flex-wrap gap-4">
              {recentBadges.map(badge => (
                <div key={badge.id} className="flex items-center gap-3 bg-[var(--bg-surface)] px-4 py-3 rounded-2xl border border-[var(--border-subtle)] shadow-sm hover:-translate-y-1 transition-transform cursor-default">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white shadow-inner ${badge.color}`}>
                    <SafeIcon name={badge.icon} className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-black text-[var(--text-main)] uppercase tracking-tight">{badge.title}</p>
                    <p className="text-[9px] font-bold text-[var(--text-secondary)] uppercase tracking-widest">{badge.date}</p>
                  </div>
                </div>
              ))}
              <button className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl border-2 border-dashed border-[var(--border-subtle)] text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest hover:border-brand-cyan hover:text-brand-cyan transition-colors">
                Ver Todas
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-8 animate-fade-in">
          {/* Team Overview Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-brand-cyan/10 border border-brand-cyan/30 p-6 rounded-3xl text-center">
              <SafeIcon name="TrendingUp" className="w-6 h-6 text-brand-cyan mx-auto mb-2" />
              <h4 className="text-[10px] font-black text-brand-cyan uppercase tracking-[0.2em] mb-1">Prod. Equipo</h4>
              <p className="text-2xl font-black text-[var(--text-main)] tracking-tighter">${mockTeamStats.totalProduction.toLocaleString()}</p>
            </div>
            <div className="bg-brand-green/10 border border-brand-green/30 p-6 rounded-3xl text-center">
              <SafeIcon name="BookOpen" className="w-6 h-6 text-brand-green mx-auto mb-2" />
              <h4 className="text-[10px] font-black text-brand-green uppercase tracking-[0.2em] mb-1">Formación</h4>
              <p className="text-2xl font-black text-[var(--text-main)] tracking-tighter">{mockTeamStats.totalTrainings} Módulos</p>
            </div>
            <div className="bg-brand-gold/10 border border-brand-gold/30 p-6 rounded-3xl text-center">
              <SafeIcon name="Users" className="w-6 h-6 text-brand-gold-dark mx-auto mb-2" />
              <h4 className="text-[10px] font-black text-brand-gold-dark uppercase tracking-[0.2em] mb-1">Impacto Comunitario</h4>
              <p className="text-2xl font-black text-[var(--text-main)] tracking-tighter">Excelente</p>
            </div>
          </div>

          {/* Team Members List */}
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-3xl overflow-hidden shadow-sm">
            <div className="p-6 border-b border-[var(--border-subtle)] flex items-center justify-between">
              <h3 className="text-sm font-black text-[var(--text-main)] uppercase tracking-[0.2em]">
                Progreso de {mockTeamStats.teamName}
              </h3>
              <button className="text-[10px] font-bold text-brand-cyan uppercase tracking-widest hover:underline">
                Descargar Reporte
              </button>
            </div>

            <div className="divide-y divide-[var(--border-subtle)]">
              {mockTeamStats.members.map(member => (
                <div key={member.id} className="p-4 md:p-6 flex flex-col md:flex-row md:items-center gap-6 hover:bg-[var(--bg-base)] transition-colors">
                  
                  {/* User Info */}
                  <div className="flex items-center gap-4 md:w-1/3 shrink-0">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-brand-navy flex items-center justify-center text-sm font-black text-white">
                        {member.avatar}
                      </div>
                      {member.topBadge && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[var(--bg-surface)] rounded-full flex items-center justify-center shadow-sm border border-[var(--border-subtle)]">
                          <SafeIcon name={member.topBadge.icon} className={`w-3 h-3 ${member.topBadge.color}`} />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-black text-[var(--text-main)] uppercase tracking-tight">{member.name}</p>
                      <p className="text-[9px] font-bold text-[var(--text-secondary)] uppercase tracking-widest mt-0.5">{member.role}</p>
                    </div>
                  </div>

                  {/* 3 Pillars Mini-Bars */}
                  <div className="flex-1 grid grid-cols-3 gap-4">
                    {/* Prod */}
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-1 text-[9px] font-black text-[var(--text-secondary)] uppercase tracking-widest">
                        <SafeIcon name="TrendingUp" className="w-3 h-3 text-brand-cyan" /> Prod
                      </div>
                      <div className="w-full h-1.5 bg-[var(--border-subtle)] rounded-full overflow-hidden">
                        <div className="h-full bg-brand-cyan rounded-full" style={{ width: `${member.stats.production}%` }} />
                      </div>
                    </div>
                    {/* Formación */}
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-1 text-[9px] font-black text-[var(--text-secondary)] uppercase tracking-widest">
                        <SafeIcon name="BookOpen" className="w-3 h-3 text-brand-green" /> Form
                      </div>
                      <div className="w-full h-1.5 bg-[var(--border-subtle)] rounded-full overflow-hidden">
                        <div className="h-full bg-brand-green rounded-full" style={{ width: `${member.stats.training}%` }} />
                      </div>
                    </div>
                    {/* Com */}
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-1 text-[9px] font-black text-[var(--text-secondary)] uppercase tracking-widest">
                        <SafeIcon name="Users" className="w-3 h-3 text-brand-gold" /> Com
                      </div>
                      <div className="w-full h-1.5 bg-[var(--border-subtle)] rounded-full overflow-hidden">
                        <div className="h-full bg-brand-gold rounded-full" style={{ width: `${member.stats.community}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Action */}
                  <div className="md:w-12 flex justify-end shrink-0">
                    <button className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:bg-brand-cyan/10 hover:text-brand-cyan transition-colors">
                      <SafeIcon name="ChevronRight" className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}